import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowUpRight } from "lucide-react";
import { SERVICES } from "../../lib/data";

const Card = ({ service, className }) => {
  const Icon = Icons[service.icon] || Icons.Sparkles;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`service-${service.id}`}
      className={`group ${className || ""}`}
    >
      <Link
        to={`/services/${service.id}`}
        data-testid={`service-link-${service.id}`}
        className="relative flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] transition-colors duration-300 hover:border-white/40 hover:bg-white/[0.04]"
      >
        {/* image */}
        <div className="relative aspect-[16/10] w-full overflow-hidden">
          <img
            src={service.image}
            alt={service.title}
            loading="lazy"
            className="h-full w-full object-cover grayscale contrast-125 opacity-80 transition-all duration-500 group-hover:scale-105 group-hover:opacity-95"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black" />
          <span className="absolute left-5 top-5 rounded-full border border-white/25 bg-black/50 px-3 py-1 font-mono text-[0.65rem] uppercase tracking-[0.2em] text-white backdrop-blur">
            {service.category}
          </span>
          <Icon
            size={18}
            strokeWidth={1.6}
            className="absolute right-5 top-5 text-white"
          />
        </div>

        <div className="flex flex-1 flex-col p-8">
          <h3 className="font-display text-2xl font-bold tracking-tight text-white md:text-3xl">
            {service.title}
          </h3>
          <p className="mt-3 text-sm leading-relaxed text-white/75">{service.desc}</p>

          <div className="mt-auto flex items-center justify-between pt-8">
            <div className="flex flex-wrap gap-2">
              {service.tags.map((t) => (
                <span
                  key={t}
                  className="rounded-full border border-white/15 px-2.5 py-1 font-mono text-[0.6rem] uppercase tracking-wider text-white/70"
                >
                  {t}
                </span>
              ))}
            </div>
            <span className="flex items-center gap-1 font-mono text-[0.7rem] uppercase tracking-[0.15em] text-white transition-colors">
              Learn more <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services"
      className="relative mx-auto max-w-[90rem] border-t border-white/10 px-6 py-24 md:px-12 md:py-32"
    >
      <div className="mb-16 grid gap-6 md:grid-cols-12 md:items-end">
        <div className="md:col-span-8">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/65">
            / Six disciplines, one brain trust
          </p>
          <h2 className="mt-5 max-w-3xl font-display text-4xl font-extrabold leading-[0.95] tracking-tighter text-white md:text-6xl">
            Every growth lever, pulled by{" "}
            <span className="font-serif-emph font-normal">people who care</span>{" "}
            about your P&amp;L.
          </h2>
        </div>
        <p className="max-w-sm text-white/75 leading-relaxed md:col-span-4">
          Built so you never again hear <em>&ldquo;that&rsquo;s the paid agency&rsquo;s problem&rdquo;</em>.
          One roof. One accountable team. Zero handoffs.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {SERVICES.map((s) => (
          <Card key={s.id} service={s} />
        ))}
      </div>
    </section>
  );
};

export default Services;
